/**
 * Class strings of the "Aspirations de carrière" questions, shared by every screen that asks them
 * or shows their answers: the performance profile's "Croissance" tab and the career meeting's plan.
 * Colours come from the `growth-*` family declared in `app.css`, never from a hex written here.
 */

import { GROWTH_PANEL_FILL } from '@/components/CareerGrowth/careerGrowthStyles';

/**
 * The outline every field of the growth forms shares: the one the library draws on its own white
 * fields (`TextInput` when `bordered`, `MultilineInput` in its white variant), given explicitly to
 * the select and the date field, which the library leaves to a different default.
 */
export const GROWTH_FIELD_BORDER = 'border-border-field';
export const GROWTH_SELECT_BORDER = `border ${GROWTH_FIELD_BORDER}`;

/**
 * A question heading, asked in the editor and read back in the results, on the `text-sm` step --
 * 14px, the size every other label of these forms wears, so a question reads as the form's own
 * voice rather than as a heading over it. Written as a step: `app.css` declares the `--text-*`
 * scale in real pixels, so the step is the size.
 */
export const GROWTH_QUESTION_TITLE = 'text-navy text-sm font-semibold leading-snug';

/**
 * Air above the first follow-up of a question, on top of the 16px its block already gaps: two more
 * 4px steps put the fields 24px below the choice chips they answer to, so a picked path reads as
 * opening the fields rather than as the first row of them.
 */
export const GROWTH_FOLLOW_UPS_OFFSET = 'mt-2';

/**
 * A follow-up's label: what names a field, or a group of chips, inside a question. Two components
 * wear it -- the library `Label` where there is one input to point `htmlFor` at, and `FollowUpLabel`
 * where the control is a group that already names itself -- so the size lives here rather than in
 * either of them.
 */
export const GROWTH_FIELD_LABEL = 'text-[14px] font-medium';

/** Captions, dates and helper copy. */
export const GROWTH_CAPTION = 'text-growth-muted text-[12px] font-medium';

/** The "not answered" / "undefined" placeholder wording. */
export const GROWTH_PLACEHOLDER = 'text-growth-slate text-[14px] italic';

/**
 * One question of the aspirations meeting, on the results section and in its editor alike. The
 * neutral question is the panel a glass section nests, with no hairline: its fill alone draws the
 * card, on the tab's glass, on the meeting plan's glass and on the tinted ground the dialog lays.
 * Only the two questions that carry a tone of their own draw an outline, to hold that tone.
 *
 * The gap between what it stacks -- title, fields, follow-ups -- is the same `gap-5` the form uses
 * between its five question blocks (`CareerAspirationForm`): a follow-up (its label, its answer, its
 * caption) reads as a question of its own, so it keeps the same air around it as one.
 */
const GROWTH_BLOCK_LAYOUT = 'flex flex-col gap-5 rounded-xl p-6 shadow-sm';
export const GROWTH_BLOCK_SURFACE = `${GROWTH_BLOCK_LAYOUT} ${GROWTH_PANEL_FILL}`;
/** What stands in the way carries the ember wash of the chips that answer it. */
export const GROWTH_BLOCK_SURFACE_BLOCKERS = `${GROWTH_BLOCK_LAYOUT} border border-growth-ember/30 bg-growth-ember-wash`;
/** The confidential question (departure risk) only swaps its tone. */
export const GROWTH_BLOCK_SURFACE_CONFIDENTIAL = `${GROWTH_BLOCK_LAYOUT} border border-navy/15 bg-gradient-to-br from-navy/5 to-growth-violet-bright/5`;

/**
 * The ground a dialog lays under the questions. The tab and the meeting plan already stand them on
 * a translucent glass panel, and that ground is what makes a card read; a dialog is plain white, so
 * it lays the panel itself rather than asking every card to draw harder.
 */
export const GROWTH_FORM_GROUND = 'rounded-2xl bg-growth-tint p-4';
